import React, { type SyntheticEvent, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import {
  type ColumnsStylesInterface,
  type GridExcelExportOptions,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { type OutputSChemaDataTable } from '../../common/dataTypes';
import { useGetModelById } from '../../hooks';
import { useGetProjectOutputSchema } from '../../hooks/tanstack/useProjectsHook';
import {
  useGetModelComparison,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { CustomTabPanel, Tab, Tabs } from '../mui-custom';
import WaitingModelOutputDialog from '../WaitingModelOutputDialog';

import ModelOutput from './ModelOutput';
import { type Report } from './ModelOutputTabs';

type ModelComparisonOutputTabsProps = {
  baseId: string;
  targetId: string;
};

const ModelTable = ({
  dataTable,
  baseId,
  targetId,
}: {
  dataTable: OutputSChemaDataTable;
  baseId: string;
  targetId: string;
}) => {
  const apiRef = useGridApiRef();

  const report = dataTable.name;

  const {
    data: modelComparisonOutput,
    isLoading: isLoadingModelComparison,
    isRunningServer: isRunningModelComparison,
    isError: isErrorModelComparison,
  } = useGetModelComparison({
    modelA: baseId,
    modelB: targetId,
  });

  const { orgId, projectId } = useGetRouteParams();

  const { data: baseModel } = useGetModelById(
    {
      modelId: Number(baseId),
      orgId,
      projectId,
    },
    {
      enabled: !!baseId && !!orgId && !!projectId && baseId !== 'actual',
    }
  );

  const { data: targetModel } = useGetModelById(
    {
      modelId: Number(targetId),
      orgId,
      projectId,
    },
    {
      enabled: !!targetId && !!orgId && !!projectId && targetId !== 'actual',
    }
  );

  const handleExportToExcel = () => {
    const colStyles: ColumnsStylesInterface = {};

    dataTable?.columns.forEach((colDef) => {
      colStyles[colDef.name] = {
        numFmt: '#,###',
        font: {},
        alignment: {},
        protection: {},
        border: {},
        fill: {
          type: 'pattern',
          pattern: 'none',
        },
      };
    });

    const options: GridExcelExportOptions = {
      fileName: `Compare_${baseId === 'actual' ? 'Actual Data' : baseModel?.name}_and_${targetId === 'actual' ? 'Actual Data' : targetModel?.name}_${report}`,
      columnsStyles: { ...colStyles },
    };

    apiRef.current.exportDataAsExcel(options);
  };

  const getErrorMessage = useMemo(() => {
    if (isErrorModelComparison) {
      return "There's some trouble running the model comparison";
    }

    return;
  }, [isErrorModelComparison]);

  const modelComparisonOutputData = modelComparisonOutput?.[report];

  return (
    <div className="flex flex-col py-4">
      <ModelOutput
        apiRef={apiRef}
        isLoading={isRunningModelComparison || isLoadingModelComparison}
        report={report}
        handleExportToExcel={handleExportToExcel}
        dataTable={dataTable}
        modelOutputData={modelComparisonOutputData}
        errorMessage={getErrorMessage}
      />
      <WaitingModelOutputDialog
        open={isRunningModelComparison}
        content="We're running your comparison, please wait."
      />
    </div>
  );
};

const ModelComparisonTabs = ({
  report,
  baseId,
  targetId,
}: {
  report: Report;
  baseId: string;
  targetId: string;
}) => {
  const { orgId, projectId } = useGetRouteParams();

  const { data: outputSchema } = useGetProjectOutputSchema({
    orgId,
    projectId,
  });

  const dataTables = outputSchema?.sections.find(
    (s) => s.name === report
  )?.data_tables;

  return (
    <>
      {dataTables?.map((d) => (
        <CustomTabPanel key={d.name} value={report} index={report}>
          <ModelTable dataTable={d} baseId={baseId} targetId={targetId} />
        </CustomTabPanel>
      ))}
    </>
  );
};

const ModelComparisonOutputTabs = ({
  baseId,
  targetId,
}: ModelComparisonOutputTabsProps) => {
  const [index, setIndex] = useState(0);
  const [report, setReport] = useState<Report>('financials');

  const { orgId, projectId } = useGetRouteParams();

  const { data: outputSchema } = useGetProjectOutputSchema(
    {
      orgId,
      projectId,
    },
    {
      enabled: !!orgId && !!projectId,
    }
  );

  const reports = outputSchema?.sections.map((s) => s.name as Report) ?? [];

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    const value = event.currentTarget.id as Report;

    setIndex(newValue);
    setReport(value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {reports && (
        <div className="flex items-center justify-between">
          <Tabs value={index} onChange={handleChange}>
            {outputSchema?.sections?.map((s) => (
              <Tab id={s.name} key={s.name} label={s.display_name} />
            ))}
          </Tabs>
        </div>
      )}
      {report && (
        <ModelComparisonTabs
          report={report}
          baseId={baseId}
          targetId={targetId}
        />
      )}
    </Box>
  );
};

export default ModelComparisonOutputTabs;
